import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SEO from "components/SEO/SEO";
import { AppDispatch, RootState } from "redux/store";
import { getActiveProperties } from "redux/reducers/Properties/PropertiesSlice";
import StayCard from "components/StayCard/StayCard";
import { SlidersHorizontal, X, ChevronDown, ChevronUp } from "lucide-react";

// ── FILTER STATE ───────────────────────────────────────────
interface Filters {
  priceMin: number;
  priceMax: number;
  neighborhood: string;
  propertyCategoryIds: number[];
  propertyTypeIds: number[];
  amenityIds: number[];
  bedrooms: number;
  bathrooms: number;
  instantBooking: boolean;
  minMonths: number;
  guestCapacity: number;
}

const DEFAULT_FILTERS: Filters = {
  priceMin: 0,
  priceMax: 5000,
  neighborhood: "",
  propertyCategoryIds: [],
  propertyTypeIds: [],
  amenityIds: [],
  bedrooms: 0,
  bathrooms: 0,
  instantBooking: false,
  minMonths: 0,
  guestCapacity: 0,
};

// ── SECTION WRAPPER ────────────────────────────────────────
const FilterSection = ({
  label,
  sectionKey,
  openSection,
  toggle,
  children,
}: {
  label: string;
  sectionKey: string;
  openSection: string | null;
  toggle: (s: string) => void;
  children: React.ReactNode;
}) => (
  <div className="border-b border-gray-100 dark:border-neutral-700">
    <button
      onClick={() => toggle(sectionKey)}
      className="w-full flex items-center justify-between px-5 py-3.5 text-left"
    >
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
      {openSection === sectionKey ? (
        <ChevronUp className="w-4 h-4 text-gray-400" />
      ) : (
        <ChevronDown className="w-4 h-4 text-gray-400" />
      )}
    </button>
    {openSection === sectionKey && (
      <div className="px-5 pb-4">{children}</div>
    )}
  </div>
);

// ── FILTER PANEL ───────────────────────────────────────────
const FilterPanel = ({
  filters,
  setFilters,
  properties,
  onClear,
}: {
  filters: Filters;
  setFilters: (f: Filters) => void;
  properties: any[];
  onClear: () => void;
}) => {
  const [openSection, setOpenSection] = useState<string | null>("price");
  const toggle = (s: string) => setOpenSection(openSection === s ? null : s);

  // Derive unique options from loaded properties
  const neighborhoods = useMemo(() => {
    const vals = properties
      .map((p) => p.neighborhood)
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
    return vals;
  }, [properties]);

  const categories = useMemo(() => {
    const map = new Map();
    properties.forEach((p) => {
      if (p.property_category?.id) {
        map.set(p.property_category.id, p.property_category.name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [properties]);

  const propertyTypes = useMemo(() => {
    const map = new Map();
    properties.forEach((p) => {
      if (p.property_type?.id) {
        map.set(p.property_type.id, p.property_type.name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [properties]);

  const amenities = useMemo(() => {
    const map = new Map();
    properties.forEach((p) => {
      (p.amenities || []).forEach((a: any) => {
        if (a.id) map.set(a.id, a.name);
      });
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [properties]);

  const toggleArr = (arr: number[], id: number) =>
    arr.includes(id) ? arr.filter((i) => i !== id) : [...arr, id];

  const hasActiveFilters =
    filters.priceMin > 0 ||
    filters.priceMax < 5000 ||
    filters.neighborhood !== "" ||
    filters.propertyCategoryIds.length > 0 ||
    filters.propertyTypeIds.length > 0 ||
    filters.amenityIds.length > 0 ||
    filters.bedrooms > 0 ||
    filters.bathrooms > 0 ||
    filters.instantBooking ||
    filters.minMonths > 0 ||
    filters.guestCapacity > 0;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-neutral-700">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#f5694b]" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Filters</span>
          {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-[#f5694b]" />}
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="text-xs text-[#f5694b] font-semibold hover:text-[#e2583d] transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Price Range */}
      <FilterSection label="Price range" sectionKey="price" openSection={openSection} toggle={toggle}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500">S/ {filters.priceMin}</span>
          <span className="text-xs text-gray-500">S/ {filters.priceMax}{filters.priceMax >= 5000 ? "+" : ""}</span>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Min</label>
            <input type="range" min={0} max={5000} step={50} value={filters.priceMin}
              onChange={(e) => setFilters({ ...filters, priceMin: Number(e.target.value) })}
              className="w-full accent-[#f5694b]" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Max</label>
            <input type="range" min={0} max={5000} step={50} value={filters.priceMax}
              onChange={(e) => setFilters({ ...filters, priceMax: Number(e.target.value) })}
              className="w-full accent-[#f5694b]" />
          </div>
        </div>
      </FilterSection>

      {/* Neighborhood */}
      {neighborhoods.length > 0 && (
        <FilterSection label="Neighborhood" sectionKey="neighborhood" openSection={openSection} toggle={toggle}>
          <select
            value={filters.neighborhood}
            onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value })}
            className="w-full text-sm border border-gray-200 dark:border-neutral-600 rounded-xl px-3 py-2.5 bg-white dark:bg-neutral-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:border-[#f5694b]"
          >
            <option value="">All neighborhoods</option>
            {neighborhoods.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </FilterSection>
      )}

      {/* Property Category */}
      {categories.length > 0 && (
        <FilterSection label="Property category" sectionKey="category" openSection={openSection} toggle={toggle}>
          <div className="space-y-2.5">
            {categories.map((cat) => (
              <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                <input type="checkbox"
                  checked={filters.propertyCategoryIds.includes(cat.id)}
                  onChange={() => setFilters({ ...filters, propertyCategoryIds: toggleArr(filters.propertyCategoryIds, cat.id) })}
                  className="w-4 h-4 accent-[#f5694b] rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{cat.name}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Property Type */}
      {propertyTypes.length > 0 && (
        <FilterSection label="Property type" sectionKey="type" openSection={openSection} toggle={toggle}>
          <div className="space-y-2.5">
            {propertyTypes.map((type) => (
              <label key={type.id} className="flex items-center gap-3 cursor-pointer group">
                <input type="checkbox"
                  checked={filters.propertyTypeIds.includes(type.id)}
                  onChange={() => setFilters({ ...filters, propertyTypeIds: toggleArr(filters.propertyTypeIds, type.id) })}
                  className="w-4 h-4 accent-[#f5694b] rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{type.name}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Bedrooms & Bathrooms */}
      <FilterSection label="Bedrooms & bathrooms" sectionKey="rooms" openSection={openSection} toggle={toggle}>
        <div className="space-y-4">
          {[
            { label: "Bedrooms", key: "bedrooms" },
            { label: "Bathrooms", key: "bathrooms" },
          ].map(({ label, key }) => (
            <div key={key}>
              <p className="text-xs text-gray-500 mb-2">{label}</p>
              <div className="flex gap-2 flex-wrap">
                {[0, 1, 2, 3, 4].map((n) => (
                  <button key={n}
                    onClick={() => setFilters({ ...filters, [key]: n })}
                    className={`w-9 h-9 rounded-full text-sm font-semibold border transition-all ${
                      filters[key as keyof Filters] === n
                        ? "bg-[#f5694b] border-[#f5694b] text-white"
                        : "border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-400 hover:border-[#f5694b] hover:text-[#f5694b]"
                    }`}
                  >
                    {n === 0 ? "Any" : `${n}+`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </FilterSection>

      {/* Guest Capacity */}
      <FilterSection label="Guest capacity" sectionKey="guests" openSection={openSection} toggle={toggle}>
        <div className="flex gap-2 flex-wrap">
          {[0, 1, 2, 3, 4, 5, 6].map((n) => (
            <button key={n}
              onClick={() => setFilters({ ...filters, guestCapacity: n })}
              className={`w-9 h-9 rounded-full text-sm font-semibold border transition-all ${
                filters.guestCapacity === n
                  ? "bg-[#f5694b] border-[#f5694b] text-white"
                  : "border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-400 hover:border-[#f5694b] hover:text-[#f5694b]"
              }`}
            >
              {n === 0 ? "Any" : `${n}+`}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Minimum Months */}
      <FilterSection label="Minimum stay" sectionKey="months" openSection={openSection} toggle={toggle}>
        <div className="flex gap-2 flex-wrap">
          {[0, 1, 2, 3, 6, 12].map((n) => (
            <button key={n}
              onClick={() => setFilters({ ...filters, minMonths: n })}
              className={`px-3 h-9 rounded-full text-sm font-semibold border transition-all ${
                filters.minMonths === n
                  ? "bg-[#f5694b] border-[#f5694b] text-white"
                  : "border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-400 hover:border-[#f5694b] hover:text-[#f5694b]"
              }`}
            >
              {n === 0 ? "Any" : `${n}mo`}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Amenities */}
      {amenities.length > 0 && (
        <FilterSection label="Amenities" sectionKey="amenities" openSection={openSection} toggle={toggle}>
          <div className="space-y-2.5 max-h-48 overflow-y-auto">
            {amenities.map((a) => (
              <label key={a.id} className="flex items-center gap-3 cursor-pointer group">
                <input type="checkbox"
                  checked={filters.amenityIds.includes(a.id)}
                  onChange={() => setFilters({ ...filters, amenityIds: toggleArr(filters.amenityIds, a.id) })}
                  className="w-4 h-4 accent-[#f5694b] rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{a.name}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/* Instant Booking */}
      <div className="px-5 py-4">
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Instant booking</p>
            <p className="text-xs text-gray-400 mt-0.5">Book without waiting for approval</p>
          </div>
          <button
            onClick={() => setFilters({ ...filters, instantBooking: !filters.instantBooking })}
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
              filters.instantBooking ? "bg-[#f5694b]" : "bg-gray-200 dark:bg-neutral-600"
            }`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
              filters.instantBooking ? "translate-x-5" : "translate-x-0"
            }`} />
          </button>
        </label>
      </div>
    </div>
  );
};

// ── MAIN PAGE ──────────────────────────────────────────────
export default function ListingPropertiesPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { activeProperties, loading } = useSelector(
    (state: RootState) => state.property
  );

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState<"default" | "price_asc" | "price_desc" | "rating">("default");

  useEffect(() => {
    if (!activeProperties || activeProperties.length === 0) {
      dispatch(getActiveProperties());
    }
  }, [dispatch]);

  // ── IN-MEMORY FILTER ──────────────────────────────────────
  const filtered = useMemo(() => {
    let result = [...(activeProperties || [])];

    if (filters.priceMin > 0 || filters.priceMax < 5000) {
      result = result.filter((p) => {
        const price = parseFloat(p.price_per_month) || 0;
        return price >= filters.priceMin && price <= filters.priceMax;
      });
    }

    if (filters.neighborhood) {
      result = result.filter(
        (p) => p.neighborhood?.toLowerCase() === filters.neighborhood.toLowerCase()
      );
    }

    if (filters.propertyCategoryIds.length > 0) {
      result = result.filter((p) =>
        filters.propertyCategoryIds.includes(p.property_category?.id)
      );
    }

    if (filters.propertyTypeIds.length > 0) {
      result = result.filter((p) =>
        filters.propertyTypeIds.includes(p.property_type?.id)
      );
    }

    if (filters.amenityIds.length > 0) {
      result = result.filter((p) =>
        filters.amenityIds.every((id) =>
          (p.amenities || []).some((a: any) => a.id === id)
        )
      );
    }

    if (filters.bedrooms > 0) {
      result = result.filter((p) => ((p as any).total_bedrooms || 0) >= filters.bedrooms);
    }

    if (filters.bathrooms > 0) {
      result = result.filter((p) => ((p as any).total_bathrooms || 0) >= filters.bathrooms);
    }

    if (filters.guestCapacity > 0) {
      result = result.filter((p) => ((p as any).guest_number || 0) >= filters.guestCapacity);
    }

    if (filters.minMonths > 0) {
      result = result.filter(
        (p) => ((p as any).minimum_months_of_booking || 0) <= filters.minMonths
      );
    }

    if (filters.instantBooking) {
      result = result.filter((p) => (p as any).instant_booking === true);
    }

    // Sort
    if (sortBy === "price_asc") {
      result.sort((a, b) => (parseFloat(a.price_per_month) || 0) - (parseFloat(b.price_per_month) || 0));
    } else if (sortBy === "price_desc") {
      result.sort((a, b) => (parseFloat(b.price_per_month) || 0) - (parseFloat(a.price_per_month) || 0));
    } else if (sortBy === "rating") {
      result.sort((a, b) => ((b as any).average?.average_review || 0) - ((a as any).average?.average_review || 0));
    }

    return result;
  }, [activeProperties, filters, sortBy]);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const activeFilterCount = [
    filters.priceMin > 0 || filters.priceMax < 5000,
    filters.neighborhood !== "",
    filters.propertyCategoryIds.length > 0,
    filters.propertyTypeIds.length > 0,
    filters.amenityIds.length > 0,
    filters.bedrooms > 0,
    filters.bathrooms > 0,
    filters.guestCapacity > 0,
    filters.minMonths > 0,
    filters.instantBooking,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900">
      <SEO
        title="Browse Student Properties in Lima, Peru"
        description="Search all verified student rooms, studios and apartments available in Lima, Peru. Filter by price, location, room type and availability."
        canonical="/properties"
      />
      {/* Header */}
      <div className="border-b border-gray-100 dark:border-neutral-800">
        <div className="container mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {loading
                ? "Loading..."
                : `${filtered.length} propert${filtered.length === 1 ? "y" : "ies"} in Lima`}
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">All active listings</p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="text-sm border border-gray-200 dark:border-neutral-700 rounded-full px-4 py-2 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:border-[#f5694b]"
            >
              <option value="default">Sort: Featured</option>
              <option value="price_asc">Price: Low to high</option>
              <option value="price_desc">Price: High to low</option>
              <option value="rating">Top rated</option>
            </select>

            <button
              onClick={() => setShowMobileFilters(true)}
              className="lg:hidden flex items-center gap-2 text-sm border border-gray-200 dark:border-neutral-700 rounded-full px-4 py-2 bg-white dark:bg-neutral-800 text-gray-700 dark:text-gray-300"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-[#f5694b] text-white text-xs flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24">
              <FilterPanel
                filters={filters}
                setFilters={setFilters}
                properties={activeProperties || []}
                onClear={clearFilters}
              />
            </div>
          </aside>

          {/* Grid */}
          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="flex justify-center items-center py-32">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#f5694b] border-t-transparent" />
              </div>
            ) : filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((item) => (
                  <div key={item.id} className="transform transition-all duration-200 hover:-translate-y-1">
                    <StayCard data={item} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-32">
                <p className="text-gray-400 text-lg mb-4">No properties match your filters</p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-[#f5694b] text-white font-semibold rounded-full text-sm hover:bg-[#e2583d] transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-neutral-800 sticky top-0 bg-white dark:bg-neutral-900 z-10">
              <span className="font-bold text-gray-900 dark:text-white">Filters</span>
              <button onClick={() => setShowMobileFilters(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <FilterPanel
                filters={filters}
                setFilters={setFilters}
                properties={activeProperties || []}
                onClear={clearFilters}
              />
            </div>
            <div className="p-4 border-t border-gray-100 dark:border-neutral-800 sticky bottom-0 bg-white dark:bg-neutral-900">
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full py-3 bg-[#f5694b] text-white font-semibold rounded-full text-sm"
              >
                Show {filtered.length} propert{filtered.length === 1 ? "y" : "ies"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}